import auth0 from "auth0-js";
import { Auth0Client } from "@auth0/auth0-spa-js";

import { getAppFeatureProp } from "./app-feature-util";
import { getAppCustomEnv } from "./get-app-custom-env";
import getPlatformConfig from "./get-platform-config";
import { Constants } from "customer-sdk/constants";

const OAUTH_EVENTS = Constants.OAUTH_EVENTS;
export class Auth0Util {
  constructor() {}
  authenticatorClient;
  auth0Config;
  management;

  socialSigninWindow;
  socialWindowManuallyClosed = false;

  async setConfig() {
    const appCustomEnv = await getAppCustomEnv();
    const platformConfig = await getPlatformConfig(window.location.hostname);
    const auth0Config = getAppFeatureProp(platformConfig.cmsAppFeatures, "auth0");
    if (appCustomEnv.NODE_ENV == "development") {
      auth0Config.value["domain"] = appCustomEnv.VUE_APP_AUTH0_DOMAIN;
      auth0Config.value["clientId"] = appCustomEnv.VUE_APP_AUTH0_CLIENT_ID;
      auth0Config.value["audience"] = appCustomEnv.VUE_APP_AUTH0_AUDIENCE;
      auth0Config.value["realm"] = appCustomEnv.VUE_APP_AUTH0_REALM;
    }
    this.auth0Config = auth0Config.value;
  }

  // eslint-disable-next-line max-lines-per-function
  async authenticate(username, password) {
    return new Promise((resolve, reject) => {
      this.authenticatorClient.client.login(
        {
          realm: this.auth0Config.realm, //connection name or HRD domain
          username: username,
          password: password,
        },
        (err, authResult) => (err ? reject({ err }) : resolve(authResult))
      );
    });
  }

  async handleCallback() {
    await this.setup();
    await this.mainClient.handleRedirectCallback(window.location.href);
    return await this.mainClient.getTokenSilently({
      detailedResponse: true,
    });
  }

  async handleGuestCallback() {
    await this.mainClient.handleRedirectCallback(window.location.href);
    return await this.mainClient.getTokenSilently({
      detailedResponse: true,
    });
  }

  async setup() {
    if (this.auth0Client && this.mainClient) return;
    await this.setConfig();
    const { domain, clientId, audience } = this.auth0Config;

    console.log("this authConfig:: ", this.auth0Config);

    this.mainClient = new Auth0Client({
      domain: domain,
      clientId: clientId,
      authorizationParams: {
        audience: audience,
        scope: "openid profile email",
        redirect_uri: `${window.location.origin}/auth/callback/`,
      },
    });
    
    this.authenticatorClient = new auth0.WebAuth({
      domain: domain,
      clientID: clientId,
      scope: "openid",
      audience: audience,
      responseType: "token",
    });
  }

  listenToSocialWindow(callback) {
    // check if client manually closed the tab
    this.clearSocialListener();
    this.sociaLlistener = setInterval(() => {
      if (!this.socialSigninWindow || this.socialSigninWindow.closed) {
        const fallbackError = !this.socialSigninWindow
          ? new Error("Failed to open new tab")
          : new Error("The browser tab was closed");
        callback({
          error: this.socialWindowManuallyClosed ? null : fallbackError,
        });
        this.clearSocialListener();
      }
    }, 1500);
  }

  clearSocialListener() {
    if (this.sociaLlistener) {
      clearInterval(this.sociaLlistener);
      this.sociaLlistener = null;
    }
  }

  cancelSocialLogin() {
    if (this.socialSigninWindow) {
      this.socialWindowManuallyClosed = true;
      this.socialSigninWindow.close();
      this.socialSigninWindow = null;
    }
    this.clearSocialListener();
  }

  async guestSocialLogin({ connection, callback }) {
    this.mainClient.loginWithRedirect({
      authorizationParams: {
        prompt: "select_account",
        connection: connection,
        redirect_uri: `${window.location.origin}/auth/guest/callback/`,
      },
      openUrl: async (url) => {
        this.socialSigninWindow = window.open(url, "_blank");
        this.socialWindowManuallyClosed = false;
        this.listenToSocialWindow(callback);
        if (this.socialSigninWindow) {
          window.addEventListener(
            OAUTH_EVENTS.AUTHENTICATION,
            (event) => {
              this.socialSigninWindow.close();
              this.socialWindowManuallyClosed = true;
              callback(event.detail);
            },
            {
              once: true, // automatically close listener after one event
            }
          );
        }
      },
    });
  }

  /**
   * Initialize Universal Login
   * @param {Object} options - Universal login options
   * @param {String=} options.prompt - Universal login prompt. Defaults to "login"
   * @param {String=} options.connection - Social connection to open
   * @param {String=} options.idToken - Social connection to open
   */
  async initUniversalLogin(options = {}) {
    const { prompt = "login", connection, idToken, } = options;
    await this.setup();
    this.mainClient.loginWithRedirect({
      authorizationParams: {
        prompt: prompt,
        connection: connection,
        id_token_hint: idToken
      }
    });
  }

  async logout(redirectUrl = window.location.origin, redirectUser = true) {
    await this.setup();
    this.mainClient.logout({
      openUrl: () => {
        if (redirectUser) {
          window.location.href = redirectUrl;
        }
      }
    });
  }

  dispatchAuthenticationEvent(detail) {
    const event = new CustomEvent(OAUTH_EVENTS.AUTHENTICATION, {
      detail
    });
    if (window.opener) {
      window.opener.dispatchEvent(event);
    }
    if (window.parent) {
      window.parent.dispatchEvent(event);
    }
    window.dispatchEvent(event);
  }

}

export default new Auth0Util();
